/**
 * Creator Profile Page
 * Detailed view of a creator's profile with stats, bio, and social links
 * Accessible from Creator Discovery page
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Videocam as VideocamIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  Instagram as InstagramIcon,
  YouTube as YouTubeIcon,
  Language as LanguageIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { useTranslation } from 'react-i18next';
import { useDarkMode, getClozedStyles } from '../../hooks/useDarkMode';
import { getCreatorById, Creator } from '../../services/creatorDiscovery';
import { useAuth } from '../../components/AuthProvider';

export default function CreatorProfile() {
  const isDarkMode = useDarkMode();
  const styles = getClozedStyles(isDarkMode);
  const { creatorId } = useParams<{ creatorId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [brandId, setBrandId] = useState<string | null>(null);
  const [creator, setCreator] = useState<Creator | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [alreadyInvited, setAlreadyInvited] = useState(false);

  useEffect(() => {
    loadBrandProfile();
  }, [user]);

  useEffect(() => {
    if (creatorId && brandId) {
      loadCreatorProfile();
      checkInvitationStatus();
    }
  }, [creatorId, brandId]);

  const loadBrandProfile = async () => {
    if (!user) {
      setError('로그인이 필요합니다.');
      setLoading(false);
      return;
    }

    try {

      const { data: sellerProfile, error: sellerError } = await supabase
        .from('seller_profile')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (sellerError || !sellerProfile) {
        setError('브랜드 프로필을 찾을 수 없습니다.');
        setLoading(false);
        return;
      }

      setBrandId(sellerProfile.id);
    } catch (err: any) {
      console.error('Load brand profile error:', err);
      setError('프로필을 불러올 수 없습니다: ' + err.message);
      setLoading(false);
    }
  };

  const loadCreatorProfile = async () => {
    if (!creatorId) return;

    try {
      setLoading(true);
      const { data, error: creatorError } = await getCreatorById(creatorId);

      if (creatorError) throw creatorError;

      setCreator(data);
    } catch (err: any) {
      setError('크리에이터 프로필을 불러올 수 없습니다: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const checkInvitationStatus = async () => {
    if (!brandId || !creatorId) return;

    try {
      const { data } = await supabase
        .from('affiliate_partnerships')
        .select('id')
        .eq('brand_id', brandId)
        .eq('creator_id', creatorId)
        .limit(1);

      setAlreadyInvited(data ? data.length > 0 : false);
    } catch (err: any) {
      console.error('Check invitation error:', err);
    }
  };

  const handleInvite = async () => {
    if (!brandId || !creatorId) return;
    if (!user) {
      setError('Not authenticated');
      return;
    }

    try {

      const { data: profile } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', user.id)
        .single();

      const { error } = await supabase.from('affiliate_partnerships').insert({
        brand_id: brandId,
        creator_id: creatorId,
        status: 'pending',
        invited_by: profile?.id,
      });

      if (error) throw error;

      setSuccess('크리에이터를 초대했습니다!');
      setAlreadyInvited(true);
    } catch (err: any) {
      setError('초대 실패: ' + err.message);
    }
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
  };

  if (loading) {
    return (
      <Box sx={{ ...styles.pageContainer, display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!creator) {
    return (
      <Box sx={styles.pageContainer}>
        <Alert severity="error">크리에이터를 찾을 수 없습니다.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={styles.pageContainer}>
      {/* Back Button */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/seller/creator-discovery')}
        sx={{ mb: 2, color: isDarkMode ? '#fff' : '#000' }}
      >
        크리에이터 발견으로 돌아가기
      </Button>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Banner Image */}
      {creator.banner_image && (
        <Paper
          sx={{
            ...styles.paper,
            height: 300,
            backgroundImage: `url(${creator.banner_image})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            mb: 3,
          }}
        />
      )}

      {/* Profile Header */}
      <Paper sx={{ ...styles.paper, p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'start', gap: 3, mb: 3 }}>
          <Avatar
            src={creator.profile_image}
            alt={creator.store_name}
            sx={{
              width: 120,
              height: 120,
              fontSize: '3rem',
              bgcolor: isDarkMode ? '#333' : '#e0e0e0',
            }}
          >
            {creator.store_name.charAt(0).toUpperCase()}
          </Avatar>

          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" fontWeight="bold" sx={{ ...styles.text, mb: 1 }}>
              {creator.store_name}
            </Typography>
            <Typography variant="body1" sx={{ ...styles.text, mb: 2, color: isDarkMode ? '#aaa' : '#666' }}>
              {creator.store_description}
            </Typography>

            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={handleInvite}
              disabled={alreadyInvited}
            >
              {alreadyInvited ? '초대됨' : '파트너 초대'}
            </Button>
          </Box>
        </Box>

        {/* Stats */}
        <Grid container spacing={3}>
          <Grid item xs={12} sm={4}>
            <Paper
              sx={{
                ...styles.paper,
                p: 2,
                textAlign: 'center',
                bgcolor: isDarkMode ? '#2d2d2d' : '#f5f5f5',
              }}
            >
              <VideocamIcon sx={{ fontSize: 40, color: isDarkMode ? '#4caf50' : '#2e7d32', mb: 1 }} />
              <Typography variant="h5" fontWeight="bold" sx={styles.text}>
                {formatNumber(creator.total_videos || 0)}
              </Typography>
              <Typography variant="body2" sx={{ color: isDarkMode ? '#aaa' : '#666' }}>
                비디오
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Paper
              sx={{
                ...styles.paper,
                p: 2,
                textAlign: 'center',
                bgcolor: isDarkMode ? '#2d2d2d' : '#f5f5f5',
              }}
            >
              <PeopleIcon sx={{ fontSize: 40, color: isDarkMode ? '#2196f3' : '#1565c0', mb: 1 }} />
              <Typography variant="h5" fontWeight="bold" sx={styles.text}>
                {formatNumber(creator.total_followers || 0)}
              </Typography>
              <Typography variant="body2" sx={{ color: isDarkMode ? '#aaa' : '#666' }}>
                팔로워
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Paper
              sx={{
                ...styles.paper,
                p: 2,
                textAlign: 'center',
                bgcolor: isDarkMode ? '#2d2d2d' : '#f5f5f5',
              }}
            >
              <TrendingUpIcon sx={{ fontSize: 40, color: isDarkMode ? '#ff9800' : '#ef6c00', mb: 1 }} />
              <Typography variant="h5" fontWeight="bold" sx={styles.text}>
                {creator.avg_engagement_rate?.toFixed(2) || 0}%
              </Typography>
              <Typography variant="body2" sx={{ color: isDarkMode ? '#aaa' : '#666' }}>
                평균 참여율
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      {/* Bio */}
      {creator.bio && (
        <Paper sx={{ ...styles.paper, p: 3, mb: 3 }}>
          <Typography variant="h6" fontWeight="bold" sx={{ ...styles.text, mb: 2 }}>
            소개
          </Typography>
          <Typography variant="body1" sx={styles.text}>
            {creator.bio}
          </Typography>
        </Paper>
      )}

      {/* Social Links */}
      {creator.social_links && Object.keys(creator.social_links).length > 0 && (
        <Paper sx={{ ...styles.paper, p: 3 }}>
          <Typography variant="h6" fontWeight="bold" sx={{ ...styles.text, mb: 2 }}>
            소셜 링크
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {creator.social_links.instagram && (
              <Button
                variant="outlined"
                startIcon={<InstagramIcon />}
                href={creator.social_links.instagram}
                target="_blank"
                sx={{
                  borderColor: isDarkMode ? '#fff' : '#000',
                  color: isDarkMode ? '#fff' : '#000',
                }}
              >
                Instagram
              </Button>
            )}
            {creator.social_links.youtube && (
              <Button
                variant="outlined"
                startIcon={<YouTubeIcon />}
                href={creator.social_links.youtube}
                target="_blank"
                sx={{
                  borderColor: isDarkMode ? '#fff' : '#000',
                  color: isDarkMode ? '#fff' : '#000',
                }}
              >
                YouTube
              </Button>
            )}
            {creator.social_links.tiktok && (
              <Button
                variant="outlined"
                startIcon={<LanguageIcon />}
                href={creator.social_links.tiktok}
                target="_blank"
                sx={{
                  borderColor: isDarkMode ? '#fff' : '#000',
                  color: isDarkMode ? '#fff' : '#000',
                }}
              >
                TikTok
              </Button>
            )}
          </Box>
        </Paper>
      )}
    </Box>
  );
}
